import { IPublicClientApplication } from '@azure/msal-browser'
import { protectedResources } from '../authConfig'
import { Advertisement } from '../models/Advertisement'
import { AllLocationsBundle } from '../models/Checkout/AllLocationsBundle'
import { AllLocationsPreview } from '../models/Checkout/AllLocationsPreview'
import { CartDeviceAdvertisement } from '../models/Checkout/CartDeviceAdvertisement'
import { ServerPaymentIntent } from '../models/Checkout/ServerPaymentIntent'
import {
  authorizedDelete,
  authorizedGet,
  authorizedPost,
} from '../utils/authUtils'

export const createPaymentIntent = async (
  organizationId: number,
  userId: string,
  instance: IPublicClientApplication,
  couponCode?: string,
): Promise<ServerPaymentIntent> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPost(
    instance,
    `${baseUrl}/cart/createPaymentIntent`,
    {
      organizationId: organizationId,
      userId: userId,
      couponCode: couponCode ?? null,
    },
    protectedResources.scopes.read,
  )
}

export const validateCoupon = async (
  code: string,
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<{ valid: boolean; reason?: string; discountDescription?: string }> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPost(
    instance,
    `${baseUrl}/coupon/validate`,
    { code, organizationId },
    protectedResources.scopes.read,
  )
}

export const confirmPayment = async (
  paymentIntentId: string,
  instance: IPublicClientApplication,
): Promise<void> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  await authorizedPost(
    instance,
    `${baseUrl}/cart/confirmPayment`,
    {
      paymentIntentId,
    },
    protectedResources.scopes.read,
  )
}

export const addAdvertisementToCart = async (
  advertisement: Advertisement,
  instance: IPublicClientApplication,
): Promise<CartDeviceAdvertisement> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPost(
    instance,
    `${baseUrl}/cart/add`,
    advertisement,
    protectedResources.scopes.read,
  )
}

export const fetchAdvertisementsInCart = async (
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<CartDeviceAdvertisement[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedGet(
    instance,
    `${baseUrl}/cart/${organizationId}/items`,
    protectedResources.scopes.read,
  )
}

export const removeItemFromCart = async (
  deviceAdvertisementId: number,
  instance: IPublicClientApplication,
): Promise<CartDeviceAdvertisement[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedDelete(
    instance,
    `${baseUrl}/cart/items/${deviceAdvertisementId}`,
    protectedResources.scopes.read,
  )
}

export const fetchAllLocationsPreview = async (
  organizationId: number,
  numberOfDays: number,
  instance: IPublicClientApplication,
): Promise<AllLocationsPreview> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedGet(
    instance,
    `${baseUrl}/cart/all-locations-preview?organizationId=${organizationId}&numberOfDays=${numberOfDays}`,
    protectedResources.scopes.read,
  )
}

export const addAllLocationsToCart = async (
  advertisement: {
    name: string
    imageUrl?: string
    designNotes: string
    adSelectionType: string
    organizationId: number
    startDateLocal?: string | null
    endDateLocal?: string | null
    timezone?: string
    numberOfDaysToRunAd?: number | null
    autoRenew: boolean
    qrEnabled: boolean
    destinationUrl?: string | null
    qrPositionXPercent?: number | null
    qrPositionYPercent?: number | null
    qrSizePercent?: number | null
  },
  instance: IPublicClientApplication,
): Promise<AllLocationsBundle> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPost(
    instance,
    `${baseUrl}/cart/add-all-locations`,
    advertisement,
    protectedResources.scopes.read,
  )
}

export const fetchAllLocationsBundlesInCart = async (
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<AllLocationsBundle[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedGet(
    instance,
    `${baseUrl}/cart/${organizationId}/all-locations-bundle`,
    protectedResources.scopes.read,
  )
}

export const removeAllLocationsBundle = async (
  advertisementId: number,
  instance: IPublicClientApplication,
): Promise<void> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  await authorizedDelete(
    instance,
    `${baseUrl}/cart/all-locations/${advertisementId}`,
    protectedResources.scopes.read,
  )
}
